import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import {
  IFakturowniaInvoiceCorrection,
  useFakturowniaInvoice,
  useInvoiceMatching,
  usePopupStateOperations,
} from 'core/hooks';
import { PopupNames } from 'core';
import {
  InvoiceDirectionPill,
  InvoiceKindPill,
  InvoiceStatusPill,
  MoneyCell,
  ILinkInvoicePaymentPopupData,
} from 'molecules';
import { PATHS } from 'router/config';

import * as S from './FakturowniaInvoices.styled';
import { CorrectionsList } from './CorrectionsList';
import { InvoiceChainSection } from './InvoiceChainSection';

/**
 * Invoice detail (TZ §3.3) — two-column layout: document facts on the left,
 * linking panel on the right. Every action funnels through `useInvoiceMatching`
 * so toasts + cache refetches are consistent with the list page.
 */
export const FakturowniaInvoiceDetail = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const { invoice, loading, permissionDenied, refetch } = useFakturowniaInvoice(
    id,
  );
  const { unlinkPayment, loading: matching } = useInvoiceMatching();

  const [showRaw, setShowRaw] = useState(false);

  // ТЗ 0004: the canonical linking flow — pick a student → agreement →
  // unpaid взнос; the backend provisions the hidden Payment when the
  // invoice is paid.
  const openLinkPopup = useCallback(() => {
    if (!invoice) return;
    const payload: ILinkInvoicePaymentPopupData = {
      invoiceId: invoice.id,
      buyerEmail: invoice.buyerEmail || undefined,
      prefillStudentId: invoice.linkedStudent?.id || undefined,
      onLinked: () => refetch(),
    };
    showPopup(PopupNames.linkInvoicePayment, payload);
  }, [invoice, showPopup, refetch]);

  const onUnlinkPayment = useCallback(async () => {
    if (!invoice) return;
    await unlinkPayment(invoice.id);
  }, [invoice, unlinkPayment]);

  if (permissionDenied) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.EmptyState>
            {t('pages.fakturownia.invoices.noAccess')}
          </S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (loading && !invoice) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (!invoice) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.EmptyState>—</S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const isIssued = invoice.direction?.toUpperCase() === 'ISSUED';
  // Phase 7: `isCorrection` is a backend shortcut for `kind === 'correction'` —
  // shorter and survives kind-enum changes.
  const isCorrection = Boolean(invoice.isCorrection);
  // Phase 12: chain flags — surface the right banner / section based on
  // which leg of the estimate→advance→final chain we're looking at.
  const isAdvance = Boolean(invoice.isAdvance);
  const isFinal = Boolean(invoice.isFinal);
  const isEstimate = Boolean(invoice.isEstimate);
  // Phase 13: prefer the canonical agreement → student chain. Fall back to
  // the legacy `linkedStudent` only when the invoice hasn't been reconciled
  // to a Payment yet (so the email-based pre-match still shows context).
  const linkedAgreement =
    invoice.agreement || invoice.payment?.agreement || null;
  const agreementStudent = linkedAgreement?.student;
  const agreementGroup = linkedAgreement?.group;
  const agreementStudentName = agreementStudent
    ? [agreementStudent.user?.firstName, agreementStudent.user?.lastName]
        .filter(Boolean)
        .join(' ')
    : '';
  const legacyStudent = invoice.linkedStudent;
  const legacyStudentName = legacyStudent
    ? [legacyStudent.user?.firstName, legacyStudent.user?.lastName]
        .filter(Boolean)
        .join(' ')
    : '';
  const hasPayment = Boolean(invoice.payment);
  const fakturowniaPaymentRows = (
    invoice.fakturowniaPayments || []
  ).filter((p): p is NonNullable<typeof p> => Boolean(p));

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.BackLink onClick={() => history.push(PATHS.financesInvoices)}>
          {t('pages.fakturownia.invoice.backToList')}
        </S.BackLink>

        <S.HeaderRow>
          <S.PageTitle>#{invoice.number}</S.PageTitle>
        </S.HeaderRow>
        <S.HeadingPills>
          <InvoiceDirectionPill direction={invoice.direction} />
          <InvoiceKindPill
            kind={invoice.kind}
            isCorrection={invoice.isCorrection}
            isAdvance={invoice.isAdvance}
            isFinal={invoice.isFinal}
            isEstimate={invoice.isEstimate}
            isQuotation={invoice.isQuotation}
          />
          <InvoiceStatusPill status={invoice.status} />
        </S.HeadingPills>

        {isCorrection && invoice.parentInvoice ? (
          <S.Banner variant="warn">
            <S.BannerTitle>
              {t('pages.fakturownia.invoice.correctionOfTitle', {
                number: invoice.parentInvoice.number,
              })}
            </S.BannerTitle>
            {invoice.correctionReason}
          </S.Banner>
        ) : null}

        {/* Phase 12: advance / final point back to their estimate parent.
            The banner is clickable so the financier can jump straight to
            the contract view. */}
        {(isAdvance || isFinal) && invoice.parentInvoice ? (
          <S.Banner
            variant="info"
            as="button"
            type="button"
            onClick={() =>
              history.push(
                PATHS.financesInvoice.replace(':id', invoice.parentInvoice!.id),
              )
            }
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              width: '100%',
              border: '1px solid #bfdbfe',
            }}
          >
            <S.BannerTitle>
              {t('pages.fakturownia.invoice.partOfOrder', {
                number: invoice.parentInvoice.number,
              })}
            </S.BannerTitle>
          </S.Banner>
        ) : null}

        <S.DetailGrid>
          <div>
            <S.Card>
              <S.CardTitle>
                {t('pages.fakturownia.invoice.documentSection')}
              </S.CardTitle>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.issueDate')}
                </S.RowLabel>
                <S.RowValue>
                  {invoice.issueDate
                    ? moment(invoice.issueDate).format('DD.MM.YYYY')
                    : '—'}
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.sellDate')}
                </S.RowLabel>
                <S.RowValue>
                  {invoice.sellDate
                    ? moment(invoice.sellDate).format('DD.MM.YYYY')
                    : '—'}
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.paymentTo')}
                </S.RowLabel>
                <S.RowValue>
                  {invoice.paymentTo
                    ? moment(invoice.paymentTo).format('DD.MM.YYYY')
                    : '—'}
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.priceNet')}
                </S.RowLabel>
                <S.RowValue>
                  <MoneyCell
                    amount={invoice.priceNet}
                    currency={invoice.currency}
                    align="left"
                  />
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.priceGross')}
                </S.RowLabel>
                <S.RowValue>
                  <MoneyCell
                    amount={invoice.priceGross}
                    currency={invoice.currency}
                    align="left"
                  />
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>{t('pages.fakturownia.invoice.paid')}</S.RowLabel>
                <S.RowValue>
                  <MoneyCell
                    amount={invoice.paid}
                    currency={invoice.currency}
                    align="left"
                  />
                </S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.outstanding')}
                </S.RowLabel>
                <S.RowValue>
                  <MoneyCell
                    amount={invoice.outstanding}
                    currency={invoice.currency}
                    highlightOutstanding
                    align="left"
                  />
                </S.RowValue>
              </S.Row>
              {isCorrection && invoice.correctionReason ? (
                <S.Row>
                  <S.RowLabel>
                    {t('pages.fakturownia.invoice.correctionReason')}
                  </S.RowLabel>
                  <S.RowValue>{invoice.correctionReason}</S.RowValue>
                </S.Row>
              ) : null}
            </S.Card>

            <S.Card>
              <S.CardTitle>
                {t('pages.fakturownia.invoice.buyerSection')}
              </S.CardTitle>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.buyerName')}
                </S.RowLabel>
                <S.RowValue>{invoice.buyerName || '—'}</S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.buyerEmail')}
                </S.RowLabel>
                <S.RowValue>{invoice.buyerEmail || '—'}</S.RowValue>
              </S.Row>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.buyerTaxNo')}
                </S.RowLabel>
                <S.RowValue>{invoice.buyerTaxNo || '—'}</S.RowValue>
              </S.Row>
            </S.Card>

            <S.Card>
              <S.CardTitle>
                {t('pages.fakturownia.invoice.sellerSection')}
              </S.CardTitle>
              <S.Row>
                <S.RowLabel>
                  {t('pages.fakturownia.invoice.sellerName')}
                </S.RowLabel>
                <S.RowValue>{invoice.sellerName || '—'}</S.RowValue>
              </S.Row>
            </S.Card>

            {!isCorrection ? (
              <CorrectionsList
                corrections={(
                  invoice.corrections || []
                ).filter((c): c is IFakturowniaInvoiceCorrection => Boolean(c))}
              />
            ) : null}

            {/* Phase 12: on the estimate's page, show the «Платежи по
                заказу» panel — advances + final invoice with a sum vs
                contract reconciliation footer. */}
            {isEstimate ? (
              <InvoiceChainSection
                advances={(invoice.advances || []).filter((a): a is NonNullable<
                  typeof a
                > => Boolean(a))}
                finalInvoice={invoice.finalInvoice ?? null}
                estimateGross={invoice.priceGross}
                currency={invoice.currency}
              />
            ) : null}

            <S.Card>
              <S.RawPayloadToggle onClick={() => setShowRaw((s) => !s)}>
                {t('pages.fakturownia.invoice.rawSection')} ·{' '}
                {showRaw
                  ? t('pages.fakturownia.invoice.rawHide')
                  : t('pages.fakturownia.invoice.rawShow')}
              </S.RawPayloadToggle>
              {showRaw && invoice.rawPayload ? (
                <S.RawPre>
                  {typeof invoice.rawPayload === 'string'
                    ? invoice.rawPayload
                    : JSON.stringify(invoice.rawPayload, null, 2)}
                </S.RawPre>
              ) : null}
            </S.Card>
          </div>

          <div>
            <S.Card>
              <S.CardTitle>
                {t('pages.fakturownia.invoice.paymentSection')}
              </S.CardTitle>

              {hasPayment && linkedAgreement ? (
                <>
                  <S.Row>
                    <S.RowLabel>
                      {t('pages.fakturownia.invoice.linkedAgreement')}
                    </S.RowLabel>
                    <S.RowValue>
                      {agreementGroup?.id ? (
                        <S.RawPayloadToggle
                          onClick={() =>
                            history.push(
                              PATHS.groupDetail.replace(
                                ':id',
                                agreementGroup.id,
                              ),
                            )
                          }
                        >
                          {linkedAgreement.agreementTitle}
                        </S.RawPayloadToggle>
                      ) : (
                        linkedAgreement.agreementTitle
                      )}
                    </S.RowValue>
                  </S.Row>
                  {agreementStudent ? (
                    <S.Row>
                      <S.RowLabel>
                        {t('pages.fakturownia.invoice.linkedStudent')}
                      </S.RowLabel>
                      <S.RowValue>
                        <S.RawPayloadToggle
                          onClick={() =>
                            history.push(
                              PATHS.studentDetail.replace(
                                ':id',
                                agreementStudent.id,
                              ),
                            )
                          }
                        >
                          <div>{agreementStudentName || '—'}</div>
                          <div style={{ color: '#64748b', fontSize: 12 }}>
                            {agreementStudent.user?.email}
                          </div>
                        </S.RawPayloadToggle>
                      </S.RowValue>
                    </S.Row>
                  ) : null}
                  {agreementGroup ? (
                    <S.Row>
                      <S.RowLabel>
                        {t('pages.fakturownia.invoice.linkedGroup')}
                      </S.RowLabel>
                      <S.RowValue>
                        <S.RawPayloadToggle
                          onClick={() =>
                            history.push(
                              PATHS.groupDetail.replace(
                                ':id',
                                agreementGroup.id,
                              ),
                            )
                          }
                        >
                          {agreementGroup.name}
                        </S.RawPayloadToggle>
                      </S.RowValue>
                    </S.Row>
                  ) : null}
                  <S.Row>
                    <S.RowLabel>
                      {t('pages.fakturownia.invoice.linkedPayment')}
                    </S.RowLabel>
                    <S.RowValue>
                      <div>
                        {invoice.payment!.date
                          ? moment(invoice.payment!.date).format('DD.MM.YYYY')
                          : '—'}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 12 }}>
                        <MoneyCell
                          amount={invoice.payment!.amountAgreement}
                          currency={invoice.payment!.currency}
                          align="left"
                        />
                      </div>
                    </S.RowValue>
                  </S.Row>
                  <S.ActionsList>
                    <S.ActionButton
                      type="button"
                      tone="danger"
                      disabled={matching}
                      onClick={onUnlinkPayment}
                    >
                      {t('pages.fakturownia.invoice.actions.unlinkPayment')}
                    </S.ActionButton>
                  </S.ActionsList>
                </>
              ) : null}

              {!hasPayment && isIssued ? (
                <>
                  <div
                    style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}
                  >
                    {t('pages.fakturownia.invoice.noPayment')}
                  </div>
                  {legacyStudent ? (
                    <S.SubTitle>
                      {t('pages.fakturownia.invoice.legacyMatchHint', {
                        name: legacyStudentName || '—',
                        email: legacyStudent.user?.email || '',
                      })}
                    </S.SubTitle>
                  ) : invoice.buyerEmail ? (
                    <S.SubTitle>
                      {t('pages.fakturownia.invoice.buyerEmailHint', {
                        email: invoice.buyerEmail,
                      })}
                    </S.SubTitle>
                  ) : null}
                  <S.ActionsList>
                    <S.ActionButton
                      type="button"
                      tone="primary"
                      disabled={matching}
                      onClick={openLinkPopup}
                    >
                      {t('pages.fakturownia.invoice.actions.linkPayment')}
                    </S.ActionButton>
                  </S.ActionsList>
                </>
              ) : null}

              {!hasPayment && !isIssued ? (
                <div style={{ fontSize: 13, color: '#94a3b8' }}>
                  {t('pages.fakturownia.invoice.noPaymentReceived')}
                </div>
              ) : null}
            </S.Card>

            {fakturowniaPaymentRows.length > 0 ? (
              <S.Card>
                <S.CardTitle>
                  {t('pages.fakturownia.invoice.fakturowniaPayments', {
                    count: fakturowniaPaymentRows.length,
                  })}
                </S.CardTitle>
                <S.Table>
                  <thead>
                    <tr>
                      <th>{t('pages.fakturownia.invoices.columns.date')}</th>
                      <th>{t('pages.fakturownia.invoices.columns.kind')}</th>
                      <th style={{ textAlign: 'right' }}>
                        {t('pages.fakturownia.invoices.columns.gross')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fakturowniaPaymentRows.map((p) => (
                      <tr key={p.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {p.paidDate
                            ? moment(p.paidDate).format('DD.MM.YYYY')
                            : '—'}
                        </td>
                        <td style={{ color: '#64748b' }}>{p.kind || '—'}</td>
                        <td>
                          <MoneyCell amount={p.price} currency={p.currency} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </S.Table>
              </S.Card>
            ) : null}
          </div>
        </S.DetailGrid>
      </S.Wrapper>
    </MainTemplate>
  );
});
